import { useEffect, useMemo, useState } from 'react';
import { ErrorBanner, Field, GrowingTextarea, Loading, Modal, PageHead } from '../../components/ui';
import { api } from '../../lib/api';
import { blankCostLine } from '../../lib/amCosts';
import {
  gapsOf,
  isSecuritySet,
  scoreAudit,
  type ControlStatus,
} from '../../lib/amRag';
import { useResource } from '../../lib/hooks';
import { AmAuditImport, exportAudit } from './AmAuditSpreadsheet';
import { formatDate, formatNumber } from '../../lib/format';
import type {
  AmAuditAnswer,
  AmAuditDetail,
  AmCostLine,
  AmRecommendation,
} from '../../lib/types';
import {
  AmProductPicker,
  costLinesFromProducts,
  type PickedProduct,
} from './AmProductPicker';
import {
  ControlDomains,
  ControlSplit,
  CostCell,
  CostLinesEditor,
  PriorityBadge,
  RiskBadge,
  ScoreDial,
  StateBadge,
} from './amUi';

const RISK_OPTIONS = ['', 'Low', 'Medium', 'High', 'Critical'] as const;

/* Worst first, which is the order a client reads a tally in. */
const RISK_ORDER = ['Critical', 'High', 'Medium', 'Low'] as const;

type Draft = { response: string; risk: string; notes: string };

/**
 * Running an audit.
 *
 * Every question in the set is shown, answered or not, with a risk rating and a
 * free-text finding beside it. The risk rating is the load-bearing field: it is
 * what turns a page of answers into a list of things to sell or fix, and it is
 * what the client list, the schedule and the pipeline all count.
 *
 * Answers are held as a local draft and saved in one request rather than on
 * every keystroke. A 50-question audit typed into 150 separate PUTs would be
 * slower, noisier and impossible to abandon halfway.
 */
export function AmAuditEditor({
  auditId,
  onBack,
  onRiskForm,
}: {
  auditId: string;
  onBack: () => void;
  onRiskForm: (auditId: string) => void;
}) {
  /* ?all=1: every section, including those switched off for this client, so
     they can be switched back on here. Everywhere else leaves them out (143). */
  const resource = useResource<AmAuditDetail>(`/am/audits/${auditId}?all=1`);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Which findings the list shows: every question, or only those flagged at
     the chosen risk levels. Set by the "only high and critical" tick or by
     clicking one of the risk pills; clicking the active pill clears it. */
  const [riskFilter, setRiskFilter] = useState<string[] | null>(null);
  const onlyRaised = riskFilter !== null;
  const pickRisk = (levels: string[]) =>
    setRiskFilter((cur) => (cur && cur.join() === levels.join() ? null : levels));
  const [recFor, setRecFor] = useState<AmAuditAnswer | null>(null);
  /** The recommendation being read, by id. Set straight after one is created
   *  as well as from the chips on a finding. */
  const [viewingRec, setViewingRec] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [importing, setImporting] = useState(false);

  const audit = resource.data;

  /* Sections that do not apply to this client, by category name (143). Kept
     here so a switch flips at once; saved straight away, not with the answers. */
  const [excluded, setExcluded] = useState<string[]>([]);
  useEffect(() => {
    if (!audit) return;
    try {
      const parsed = audit.ExcludedCategories ? (JSON.parse(audit.ExcludedCategories) as unknown) : [];
      setExcluded(Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === 'string') : []);
    } catch {
      setExcluded([]);
    }
  }, [audit]);

  /* Rebuild the draft whenever the audit reloads, but not while there are
     unsaved edits — a background reload must not silently discard typing. */
  useEffect(() => {
    if (!audit || dirty) return;
    const next: Record<string, Draft> = {};
    for (const answer of audit.answers) {
      next[answer.AmQuestionId] = {
        response: answer.Response ?? '',
        risk: answer.Risk ?? '',
        notes: answer.Notes ?? '',
      };
    }
    setDrafts(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audit]);

  const items = useMemo(
    () =>
      (audit?.answers ?? []).map((a) => ({
        ...a,
        Response: drafts[a.AmQuestionId]?.response ?? a.Response,
        Risk: drafts[a.AmQuestionId]?.risk ?? a.Risk,
        Notes: drafts[a.AmQuestionId]?.notes ?? a.Notes,
      })),
    [audit, drafts],
  );

  /* The questions that count: everything in a section that applies. */
  const inScope = useMemo(
    () => items.filter((i) => !excluded.includes(i.Category ?? 'Other')),
    [items, excluded],
  );

  const answered = inScope.filter(
    (i) => (i.Response ?? '').trim() || (i.Risk ?? '').trim() || (i.Notes ?? '').trim(),
  ).length;

  const riskCounts = useMemo(() => {
    const counts: Record<string, number> = { Critical: 0, High: 0, Medium: 0, Low: 0 };
    for (const item of inScope) if (item.Risk && item.Risk in counts) counts[item.Risk]!++;
    return counts;
  }, [inScope]);

  if (resource.loading) return <Loading label="Loading the audit" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;
  if (!audit) return null;

  const isSecurity = isSecuritySet(audit.SetName);

  function update(questionId: string, patch: Partial<Draft>) {
    setDrafts((prev) => ({
      ...prev,
      [questionId]: { response: '', risk: '', notes: '', ...prev[questionId], ...patch },
    }));
    setDirty(true);
  }

  async function save(): Promise<boolean> {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/am/audits/${auditId}/answers`, {
        answers: Object.entries(drafts).map(([questionId, draft]) => ({
          questionId,
          ...draft,
        })),
      });
      setDirty(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the answers');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function setStatus(status: 'Draft' | 'Completed') {
    /* Save first: marking an audit complete with unsaved answers would record
       a completion that does not match what is on screen. */
    if (dirty && !(await save())) return;
    await api.put(`/am/audits/${auditId}`, { status });
    resource.reload();
    if (status === 'Completed' && isSecurity) setShowResults(true);
  }

  const shown = riskFilter
    ? inScope.filter((i) => i.Risk !== null && riskFilter.includes(i.Risk))
    : inScope;
  /* Every section is listed, switched off or not, so it can be switched back
     on — except under "only high and critical", which is a list of findings. */
  const categories = [
    ...new Set((onlyRaised ? shown : items).map((i) => i.Category ?? 'Other')),
  ];

  async function toggleSection(category: string) {
    const before = excluded;
    const next = before.includes(category)
      ? before.filter((c) => c !== category)
      : [...before, category];
    setExcluded(next);
    try {
      await api.put(`/am/audits/${auditId}`, { excludedCategories: next });
    } catch (err) {
      setExcluded(before);
      setError(err instanceof Error ? err.message : 'Could not change the section');
    }
  }

  /* Recommendations this audit produced, indexed by the finding they came
     from, so a question can say it already has one without a request per row.
     One entered against the audit but not against a particular question is
     still on the client's own tab — it just has no finding here to hang off. */
  const recsByQuestion = new Map<string, AmRecommendation[]>();
  for (const rec of audit.recommendations ?? []) {
    if (!rec.AmQuestionId) continue;
    recsByQuestion.set(rec.AmQuestionId, [
      ...(recsByQuestion.get(rec.AmQuestionId) ?? []),
      rec,
    ]);
  }
  const viewing = (audit.recommendations ?? []).find(
    (r) => r.AmRecommendationId === viewingRec,
  );

  return (
    <>
      <div className="row-between wrap mb-4 no-print">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>
          ← Back to the client
        </button>
        <div className="row wrap">
          {dirty && <span className="badge badge-warning">Unsaved changes</span>}
          <label className="checkbox">
            <input
              type="checkbox"
              checked={riskFilter?.join() === 'Critical,High'}
              onChange={(e) => setRiskFilter(e.target.checked ? ['Critical', 'High'] : null)}
            />
            Only high and critical
          </label>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving || !dirty}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {isSecurity && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowResults(true)}
            >
              Results
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => window.print()}
          >
            Print audit
          </button>
          {/* Out to a spreadsheet and back, for an audit answered by somebody
              who is not sitting in this app. The export carries the question
              ids the import matches on — see AmAuditSpreadsheet.tsx. */}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => exportAudit(audit, inScope)}
            title="One row per question, with the answers so far. Opens in Excel."
          >
            Export answers
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setImporting(true)}
            title="Read a filled-in export back in"
          >
            Import answers
          </button>
          {audit.Status === 'Completed' ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => void setStatus('Draft')}
            >
              Reopen
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => void setStatus('Completed')}
            >
              Mark complete
            </button>
          )}
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      {/* The working surface. It is a form — selects, inputs and a button on
          every row — and a form is not a document, so paper gets the sheet
          rendered below this instead of a printout of the controls. */}
      <div className="no-print">
        <PageHead
          title={audit.Name ?? 'Audit'}
          lede={[
            audit.ClientName,
            audit.SetName,
            `${answered} of ${inScope.length} answered`,
            audit.Auditor,
          ]
            .filter(Boolean)
            .join(' · ')}
        />

        <div className="card mb-4">
          <div className="card-body tight row-between wrap">
            <span className="row wrap">
              <StateBadge status={audit.Status} />
              <span className="xs muted mono">{formatDate(audit.AuditDate)}</span>
            </span>
            <span className="row wrap">
              {/* Each pill filters the list to that risk level; click again to clear. */}
              {(
                [
                  ['Critical', 'badge-critical'],
                  ['High', 'badge-serious'],
                  ['Medium', 'badge-warning'],
                  ['Low', 'badge-good'],
                ] as const
              ).map(([level, cls]) =>
                riskCounts[level]! > 0 ? (
                  <button
                    key={level}
                    type="button"
                    className={`badge ${cls} badge-pick${riskFilter?.join() === level ? ' is-active' : ''}`}
                    aria-pressed={riskFilter?.join() === level}
                    title={`Show only ${level.toLowerCase()} findings`}
                    onClick={() => pickRisk([level])}
                  >
                    {riskCounts[level]} {level.toLowerCase()}
                  </button>
                ) : null,
              )}
              {riskFilter && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRiskFilter(null)}>
                  Show all
                </button>
              )}
              {Object.values(riskCounts).every((n) => n === 0) && (
                <span className="xs subtle">no risks flagged yet</span>
              )}
            </span>
          </div>
        </div>

        {audit.Summary && (
          <div className="card mb-4">
            <div className="card-body">
              <span className="eyebrow">Summary</span>
              <p className="mt-2">{audit.Summary}</p>
            </div>
          </div>
        )}

        <section className="card">
          <div className="card-head">
            <div className="grow">
              <span className="eyebrow">Findings</span>
              <h2>Questions</h2>
              <p className="sub">
                Answer each question, flag a risk where the environment falls short, then turn any
                finding into a costed recommendation.
              </p>
            </div>
          </div>
          <div className="card-body">
            {categories.map((category) => {
              const off = excluded.includes(category);
              const count = items.filter((i) => (i.Category ?? 'Other') === category).length;
              return (
              <div key={category}>
                <div className="am-category-head">
                  <div className={`am-category${off ? ' is-off' : ''}`}>{category}</div>
                  <label className="am-section-switch" title="Switch off a section that does not apply to this client">
                    <input type="checkbox" checked={!off} onChange={() => void toggleSection(category)} />
                    {off ? 'Does not apply' : 'Applies'}
                  </label>
                </div>
                {off && (
                  <p className="xs muted am-section-off">
                    Switched off for this client — its {count} question{count === 1 ? ' is' : 's are'}{' '}
                    left out of the audit, the score and the review. Any answers already given are
                    kept and come back if you switch it on.
                  </p>
                )}
                {!off && shown
                  .filter((i) => (i.Category ?? 'Other') === category)
                  .map((item) => {
                    const draft = drafts[item.AmQuestionId] ?? {
                      response: '',
                      risk: '',
                      notes: '',
                    };
                    return (
                      <div key={item.AmQuestionId} className="am-question">
                        <div className="small strong">
                          {item.Text}
                          {item.IsCustom && <span className="badge badge-neutral ml-2">Custom</span>}
                        </div>
                        {item.Guidance && <div className="xs muted mt-2">{item.Guidance}</div>}
                        <div className="am-question-controls">
                          {item.QuestionType === 'yesno' || item.QuestionType === 'yesnounknown' ? (
                            <select
                              className="select"
                              value={draft.response}
                              onChange={(e) =>
                                update(item.AmQuestionId, { response: e.target.value })
                              }
                              aria-label="Response"
                            >
                              <option value="">— select —</option>
                              <option>Yes</option>
                              <option>Partial</option>
                              <option>No</option>
                              {/* Only where the question allows it — see migration 141. */}
                              {item.QuestionType === 'yesnounknown' && <option>Unknown</option>}
                              <option>N/A</option>
                            </select>
                          ) : item.QuestionType === 'scale' ? (
                            <select
                              className="select"
                              value={draft.response}
                              onChange={(e) =>
                                update(item.AmQuestionId, { response: e.target.value })
                              }
                              aria-label="Rating"
                            >
                              <option value="">— rate 1-5 —</option>
                              {[1, 2, 3, 4, 5].map((n) => (
                                <option key={n}>{n}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              className="input"
                              placeholder="Response…"
                              value={draft.response}
                              onChange={(e) =>
                                update(item.AmQuestionId, { response: e.target.value })
                              }
                              aria-label="Response"
                            />
                          )}
                          <select
                            className="select"
                            value={draft.risk}
                            onChange={(e) => update(item.AmQuestionId, { risk: e.target.value })}
                            aria-label="Risk"
                          >
                            {RISK_OPTIONS.map((r) => (
                              <option key={r} value={r}>
                                {r || 'No risk'}
                              </option>
                            ))}
                          </select>
                          {/* Grows as it is typed into rather than scrolling
                              sideways, so a long finding can be read back. */}
                          <GrowingTextarea
                            placeholder="Notes and findings…"
                            value={draft.notes}
                            onChange={(e) => update(item.AmQuestionId, { notes: e.target.value })}
                            aria-label="Notes"
                          />
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            title="Turn this finding into a recommendation"
                            onClick={() => setRecFor(item)}
                          >
                            + Rec
                          </button>
                        </div>

                        {/* What this finding has already produced. Below the
                            controls rather than inside them: that row is a
                            fixed grid of inputs, and a finding can carry more
                            than one recommendation. */}
                        {(recsByQuestion.get(item.AmQuestionId) ?? []).length > 0 && (
                          <div className="row wrap mt-2">
                            <span className="xs muted">Recommended:</span>
                            {(recsByQuestion.get(item.AmQuestionId) ?? []).map((rec) => (
                              <button
                                key={rec.AmRecommendationId}
                                type="button"
                                className="btn btn-secondary btn-sm"
                                title="Open this recommendation"
                                onClick={() => setViewingRec(rec.AmRecommendationId)}
                              >
                                {rec.Title ?? 'Untitled recommendation'}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
              );
            })}
          </div>
        </section>

      </div>

      {importing && (
        <AmAuditImport
          answers={inScope}
          onClose={() => setImporting(false)}
          onApply={(incoming) => {
            /* Merged into the drafts rather than replacing them: a file that
               answers half the audit must not blank the half somebody has
               just typed. */
            setDrafts((prev) => ({ ...prev, ...incoming }));
            setDirty(true);
            setImporting(false);
          }}
        />
      )}

      <AuditPrintSheet
        audit={audit}
        items={shown}
        answered={answered}
        total={inScope.length}
        riskCounts={riskCounts}
        filtered={riskFilter}
        scored={isSecurity}
      />

      {recFor && (
        <RecommendationFromFinding
          auditId={auditId}
          clientId={audit.AmClientId}
          finding={recFor}
          draft={drafts[recFor.AmQuestionId]}
          onClose={() => setRecFor(null)}
          /* Straight into the thing that was just written. Before this it
             closed onto the question list with nothing to show for it, and the
             only way to check what had been created was to leave the audit for
             the client's Recommendations tab. */
          onCreated={(id) => {
            setRecFor(null);
            resource.reload();
            setViewingRec(id);
          }}
        />
      )}

      {viewing && (
        <RecommendationViewer
          rec={viewing}
          clientName={audit.ClientName}
          onClose={() => setViewingRec(null)}
        />
      )}

      {showResults && (
        <AuditResultsModal
          audit={{ ...audit, answers: inScope }}
          onClose={() => setShowResults(false)}
          onRiskForm={() => {
            setShowResults(false);
            onRiskForm(auditId);
          }}
        />
      )}
    </>
  );
}

/**
 * The audit as a document.
 *
 * The editor is a form, and printing a form gives you a page of dropdowns. So
 * paper gets its own rendering: the same answers, drafts included, laid out as
 * a header, a tally and one table per category. It is in the DOM at all times
 * and hidden on screen — the alternative is a second copy of a fifty-question
 * audit sitting under the editor, which is noise for everyone who is working
 * rather than exporting.
 *
 * The screen filter carries over. Somebody who has narrowed the page to the
 * high and critical findings and then hits print wants those findings, and the
 * sheet says so in its header rather than quietly printing a partial audit as
 * if it were the whole thing.
 */
function AuditPrintSheet({
  audit,
  items,
  answered,
  total,
  riskCounts,
  filtered,
  scored,
}: {
  audit: AmAuditDetail;
  items: AmAuditAnswer[];
  answered: number;
  total: number;
  riskCounts: Record<string, number>;
  /** The risk levels the printout is limited to, or null for all of it. */
  filtered: string[] | null;
  scored: boolean;
}) {
  const categories = [...new Set(items.map((i) => i.Category ?? 'Other'))];
  const control = scoreAudit(items);
  const raised = RISK_ORDER.filter((r) => (riskCounts[r] ?? 0) > 0);

  return (
    <article className="am-doc am-audit-print">
      <header className="am-doc-head">
        <div>
          <div className="am-doc-brand">Audit report</div>
          <h2>{audit.Name ?? 'Audit'}</h2>
          <p className="small muted mt-2">{audit.ClientName}</p>
        </div>
        <div className="am-doc-meta">
          {audit.SetName && <div>{audit.SetName}</div>}
          <div>{formatDate(audit.AuditDate) || 'No date'}</div>
          {audit.Auditor && <div>Audited by {audit.Auditor}</div>}
          <div>{audit.Status ?? 'Draft'}</div>
        </div>
      </header>

      <table className="am-doc-table">
        <tbody>
          <tr>
            <th style={{ width: '30%' }}>Answered</th>
            <td>
              {answered} of {total} questions
            </td>
          </tr>
          {scored && control.scored > 0 && (
            <tr>
              <th>Control score</th>
              <td>
                {control.score}% — {control.counts['in-place'] ?? 0} in place,{' '}
                {control.counts.partial ?? 0} partial, {control.counts.gap ?? 0} gaps, across{' '}
                {control.scored} scored questions
              </td>
            </tr>
          )}
          <tr>
            <th>Risks flagged</th>
            <td>
              {raised.length === 0
                ? 'None'
                : raised.map((r) => `${riskCounts[r]} ${r.toLowerCase()}`).join(', ')}
            </td>
          </tr>
          {filtered && (
            <tr>
              <th>Scope of this printout</th>
              <td>
                {filtered.map((l) => l.toLowerCase()).join(' and ').replace(/^./, (c) => c.toUpperCase())}{' '}
                findings only — this is not the full question set.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {audit.Summary && (
        <>
          <div className="am-doc-title">Summary</div>
          <p>{audit.Summary}</p>
        </>
      )}

      {items.length === 0 ? (
        <p className="mt-6">Nothing to report on this audit.</p>
      ) : (
        categories.map((category) => (
          <div key={category}>
            <div className="am-doc-title">{category}</div>
            <table className="am-doc-table">
              <thead>
                <tr>
                  <th style={{ width: '44%' }}>Question</th>
                  <th style={{ width: '14%' }}>Response</th>
                  <th style={{ width: '12%' }}>Risk</th>
                  <th>Finding</th>
                </tr>
              </thead>
              <tbody>
                {items
                  .filter((i) => (i.Category ?? 'Other') === category)
                  .map((item) => (
                    <tr key={item.AmQuestionId}>
                      <td>{item.Text}</td>
                      <td>{item.Response?.trim() || '—'}</td>
                      <td>{item.Risk?.trim() || '—'}</td>
                      <td>{item.Notes?.trim() || '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </article>
  );
}

/**
 * The control-review scorecard.
 *
 * Only meaningful for the yes/no/partial security templates, which is why it is
 * behind a button rather than always on the page: scoring a free-text
 * environment audit as a percentage would be inventing a number.
 */
function AuditResultsModal({
  audit,
  onClose,
  onRiskForm,
}: {
  audit: AmAuditDetail;
  onClose: () => void;
  onRiskForm: () => void;
}) {
  const { counts, scored, score } = scoreAudit(audit.answers);
  const gaps = gapsOf(audit.answers).length;

  return (
    <Modal
      title="Security review results"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          {gaps > 0 ? (
            <button type="button" className="btn btn-primary" onClick={onRiskForm}>
              Risk acceptance form ({gaps} items)
            </button>
          ) : (
            <span className="badge badge-good">No gaps — nothing to accept</span>
          )}
        </>
      }
    >
      {scored === 0 ? (
        <p className="small muted">
          Nothing in this audit has been answered Yes, No or Partial yet, so there is no control
          score to show. Answer the questions first.
        </p>
      ) : (
        <>
          <div className="am-score-row">
            <ScoreDial score={score} />
            <div className="grow">
              <ControlSplit counts={counts as Record<ControlStatus, number>} />
            </div>
          </div>
          <ControlDomains items={audit.answers} />
        </>
      )}
    </Modal>
  );
}

/** Create a recommendation from the finding in front of you, with the finding
 *  text and the risk rating already carried across. */
function RecommendationFromFinding({
  auditId,
  clientId,
  finding,
  draft,
  onClose,
  onCreated,
}: {
  auditId: string;
  clientId: string;
  finding: AmAuditAnswer;
  draft: Draft | undefined;
  onClose: () => void;
  onCreated: (recommendationId: string) => void;
}) {
  const response = draft?.response ?? finding.Response ?? '';
  const notes = draft?.notes ?? finding.Notes ?? '';
  const risk = draft?.risk ?? finding.Risk ?? '';

  const [title, setTitle] = useState('');
  const [findingText, setFindingText] = useState(
    [finding.Text, response && `Response: ${response}`, notes]
      .filter(Boolean)
      .join(' — '),
  );
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<AmCostLine[]>([
    {
      ...blankCostLine(),
      /* Seed the priority from the auditor's risk rating: a Critical finding
         should not arrive as a Medium recommendation by default. */
      priority: risk === 'Critical' || risk === 'High' ? risk : 'Medium',
    },
  ]);
  const [products, setProducts] = useState<PickedProduct[]>([]);
  const [effort, setEffort] = useState('');
  const [hours, setHours] = useState('0');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<{ AmRecommendationId: string }>('/am/recommendations', {
        clientId,
        auditId,
        questionId: finding.AmQuestionId,
        title: title.trim(),
        finding: findingText,
        description,
        costLines: lines,
        products: products.map((p) => ({ productId: p.productId, quantity: p.quantity })),
        effort,
        hours: Number(hours) || 0,
        status: 'Proposed',
      });
      onCreated(created.AmRecommendationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the recommendation');
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Recommendation from this finding"
      onClose={onClose}
      size="xl"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !title.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Add recommendation'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="row mb-4">
        <span className="xs muted">Auditor risk</span>
        <RiskBadge risk={risk || null} />
      </div>
      <div className="form-grid">
        <Field label="Title">
          <input
            className="input"
            placeholder="e.g. Deploy MFA across all accounts"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field label="Finding" hint="Carried from the audit; edit it into the client's language.">
          <textarea
            className="textarea"
            rows={3}
            value={findingText}
            onChange={(e) => setFindingText(e.target.value)}
          />
        </Field>
        <Field label="Description and scope">
          <textarea
            className="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        {/* What to buy, before what it costs: a finding becomes a
            recommendation to deploy something specific, and the price follows
            from the catalogue rather than from memory. */}
        <AmProductPicker
          items={products}
          onChange={setProducts}
          onApplyAsCost={(items) =>
            setLines(costLinesFromProducts(items, lines[0]?.priority ?? 'Medium'))
          }
        />
        <CostLinesEditor lines={lines} onChange={setLines} />
        <Field label="Effort or timeline">
          <input
            className="input"
            placeholder="e.g. two weeks"
            value={effort}
            onChange={(e) => setEffort(e.target.value)}
          />
        </Field>
        <Field label="Estimated hours">
          <input
            className="input"
            type="number"
            min="0"
            step="0.25"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/* =========================================================================
   Reading a recommendation, without leaving the audit
   ========================================================================= */

/**
 * One recommendation, read-only.
 *
 * Opened straight after a finding is turned into one, and from the chip on any
 * finding that already has one. Until this existed a recommendation created
 * here disappeared the moment the dialog closed: the only way to see what had
 * been written — or whether the price came out right — was to leave the audit
 * for the client's Recommendations tab and find it in the list.
 *
 * READ-ONLY ON PURPOSE. Editing lives on that tab, where the status, the
 * cost lines and the product list are all in one editor and changing a status
 * ticks the product matrix. A second editor here would be a second place for
 * those rules to live, and the question this answers is "what did I just
 * write", not "let me redo it".
 */
function RecommendationViewer({
  rec,
  clientName,
  onClose,
}: {
  rec: AmRecommendation;
  clientName: string;
  onClose: () => void;
}) {
  const products = rec.Products ?? [];

  return (
    <Modal
      title={rec.Title ?? 'Recommendation'}
      onClose={onClose}
      footer={
        <>
          {/* No possessive: half the client names in this database end in s
              ("Adventure Works's" reads as a typo) and the apostrophe rule for
              those is not worth encoding to save three words. */}
          <span className="xs muted grow">
            Editable on the Recommendations tab for {clientName}.
          </span>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Back to the audit
          </button>
        </>
      }
    >
      <div className="row wrap mb-4">
        <StateBadge status={rec.Status} />
        <PriorityBadge priority={rec.Priority} />
        {rec.Hours ? <span className="xs muted">{rec.Hours} hrs</span> : null}
        {rec.Effort && <span className="xs muted">{rec.Effort}</span>}
      </div>

      {rec.Finding && (
        <Field label="Finding">
          <div className="am-boilerplate">{rec.Finding}</div>
        </Field>
      )}

      {rec.Description && (
        <Field label="Description and scope">
          <div className="am-boilerplate">{rec.Description}</div>
        </Field>
      )}

      <Field label="Price">
        {/* Stacked by type rather than summed. A single figure would hide the
            difference between $2,000 once and $2,000 a month, which is the
            whole reason a recommendation carries cost LINES. */}
        {rec.CostLines.length > 0 ? <CostCell rec={rec} /> : <span className="subtle">—</span>}
      </Field>

      {products.length > 0 && (
        <Field label="From the catalogue" hint="Priced when they were attached, not looked up live.">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Vendor</th>
                  <th className="num" style={{ width: 60 }}>Qty</th>
                  <th className="num" style={{ width: 90 }}>Unit</th>
                  <th style={{ width: 80 }}>Bills</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.ProductId}>
                    <td className="strong">{p.Name}</td>
                    <td className="xs muted">{p.Vendor ?? '—'}</td>
                    <td className="num">{p.Quantity}</td>
                    <td className="num">{formatNumber(p.UnitPrice, 'currency')}</td>
                    <td className="xs">{p.PeriodType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Field>
      )}
    </Modal>
  );
}
